/**
 * Збірка конфігурації для useHead з SEO-даних.
 */

import {
  createBasicMetaTags,
  createOpenGraphTags,
  createTwitterCardTags,
  createCanonicalAndAlternateLinks,
} from './seoHelpers'
import { createBreadcrumbSchema } from './schemaHelpers'
import { createMultilingualSchema } from './localeHelpers'
import { getSupportedLocales } from './localeValidation'
import type { BaseSeoConfig } from './seoHelpers'
import type { ResolvedSeoData, SEOData } from '../types'
import type { LocalizedContent } from './localeHelpers'

const DEFAULT_BASE_URL = 'https://bibleawr.com'

export interface HeadBuilderContext {
  getConfigForLocale: (locale: string) => BaseSeoConfig
  routePath: string
  getSupportedLocales?: () => readonly string[]
}

export class SeoHeadBuilder {
  static build(
    resolved: ResolvedSeoData,
    finalData: SEOData,
    context: HeadBuilderContext
  ): Record<string, unknown> {
    const { getConfigForLocale, routePath, getSupportedLocales: getLocales } = context
    const baseConfig = getConfigForLocale(resolved.locale)

    const meta = SeoHeadBuilder.buildMeta(resolved, finalData, getConfigForLocale)
    const headConfig: Record<string, unknown> = {
      title: resolved.title,
      htmlAttrs: { lang: resolved.locale },
      meta,
    }

    const links = createCanonicalAndAlternateLinks(
      resolved.url ?? '',
      finalData.alternateUrls,
      routePath
    )
    links.push({ rel: 'ai-sitemap', href: `${DEFAULT_BASE_URL}/llms.txt` })
    if (links.length > 0) headConfig.link = links

    const scripts = SeoHeadBuilder.buildScripts(finalData, resolved, baseConfig, getLocales)
    if (scripts.length > 0) headConfig.script = scripts

    return headConfig
  }

  private static buildMeta(
    resolved: ResolvedSeoData,
    finalData: SEOData,
    getConfigForLocale: (locale: string) => BaseSeoConfig
  ): Array<{ name?: string; property?: string; content: string }> {
    const basic = createBasicMetaTags({
      title: resolved.title,
      description: resolved.description,
      robots: finalData.robots,
    })
    const og = createOpenGraphTags(
      {
        title: resolved.title,
        description: resolved.description,
        url: resolved.url,
        image: resolved.image,
        type: finalData.type,
        locale: resolved.locale,
        alternateUrls: finalData.alternateUrls,
      },
      getConfigForLocale
    )
    const baseConfig = getConfigForLocale(resolved.locale)
    const twitter = createTwitterCardTags(
      {
        title: resolved.title,
        description: resolved.description,
        image: resolved.image,
      },
      baseConfig
    )
    const meta = [...basic, ...og, ...twitter]
    const keywords = resolved.keywords?.length ? resolved.keywords : finalData.keywords
    if (keywords?.length) {
      meta.push({ name: 'keywords', content: keywords.join(', ') })
    }
    return meta
  }

  private static buildScripts(
    finalData: SEOData,
    resolved: ResolvedSeoData,
    baseConfig: BaseSeoConfig,
    getSupportedLocales?: () => readonly string[]
  ): Array<{ type: string; innerHTML: string }> {
    const scripts: Array<{ type: string; innerHTML: string }> = []

    if (finalData.breadcrumbs?.length) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(createBreadcrumbSchema(finalData.breadcrumbs)),
      })
    }

    if (finalData.schema) {
      let schema = finalData.schema
      const hasLocalized =
        (typeof finalData.title === 'object' && Object.keys(finalData.title || {}).length > 0) ||
        (typeof finalData.slug === 'object' && Object.keys(finalData.slug || {}).length > 0)
      if (hasLocalized && getSupportedLocales) {
        const localizedContent: LocalizedContent = {
          title: typeof finalData.title === 'object' ? finalData.title : undefined,
          description: typeof finalData.description === 'object' ? finalData.description : undefined,
          slug: typeof finalData.slug === 'object' ? finalData.slug : undefined,
        }
        schema = createMultilingualSchema(
          schema,
          localizedContent,
          resolved.locale,
          baseConfig.baseUrl ?? DEFAULT_BASE_URL,
          getSupportedLocales()
        )
      }
      scripts.push({ type: 'application/ld+json', innerHTML: JSON.stringify(schema) })
    }

    return scripts
  }
}
